import React from "react";
//#region types
import { AgentCaseSummary, AgentSentCase, AgentTurn, SupportCaseContent } from "models/agent/agentContract";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
import { AgentTrackingContext, AgentTrackingValue, conversationEnding, trackAgent } from "./agentTracking";

// The conversation outlives the popper, which unmounts on close: a reply that lands while it is
// closed is kept. sessionStorage, so it is per tab (SessionHelper is localStorage despite its name).

const StoragePrefix = "agentConversation:";
const StorageKeyPrefix = `${StoragePrefix}v3:`;
const IdleExpiryMs = 15 * 60 * 1000;
const MaxTurns = 50;
// The idle check runs just after expiry, so the conversation is past it when checked.
const IdleCheckDelayMs = 1000;

const FirstTurnId = 1;

/** A call is in flight. `since` lets a reopened popper pick the wait up where it was. */
export interface AgentPending {
  kind: "message" | "prepare" | "send";
  since: number;
}

/** What ended a conversation that was not picked up again. */
type AgentEndedBy = "idle" | "logout" | "new_question";

/** What the usage tracking counts over a conversation. Codes and counts only, never text. */
export interface AgentConversationTracking {
  /** For tracking only: ties a conversation's events together across popper opens. */
  conversationId: string;
  popperOpens: number;
  messageCount: number;
  lastActionId?: string;
  /** The last answer's outcome, as the server sent it. */
  lastOutcome?: string;
  caseId?: number;
  /** The case was put in front of her to send; whether she did is the ending. */
  reviewShown?: boolean;
  agentUnavailable?: boolean;
  /** `conversation.ended` was sent; it is sent once. */
  endedReported?: boolean;
}

export interface AgentConversationState {
  turns: AgentTurn[];
  pending: AgentPending | null;
  sessionId: string | null;
  /** The agent ended the session: the id stays for the case summary, and her next message opens a new one. */
  sessionEnded?: boolean;
  /** Her question moved to a new agent session, so no one session holds all of it: its case is sent without one. */
  sessionRestarted?: boolean;
  /** The case as it would go to support, shown for her to look over before she sends it. */
  review: SupportCaseContent | null;
  /** The last try to send the case failed; she can send it again. */
  sendFailed: boolean;
  sentCase: AgentSentCase | null;
  lastActivityAt: number;
  /** An agent reply landed while nobody had the conversation open: the help button shows a dot. */
  unread: boolean;
  tracking: AgentConversationTracking;
}

type StoredConversation = Omit<AgentConversationState, "pending" | "turns" | "tracking"> & {
  turns: (Omit<AgentTurn, "at"> & { at: string })[];
  tracking?: AgentConversationTracking;
};

const readStorage = (key: string): StoredConversation | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as StoredConversation) : null;
  } catch {
    return null;
  }
};

const writeStorage = (key: string, state: AgentConversationState) => {
  try {
    const { pending: _pending, ...stored } = state;
    window.sessionStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // Storage full or blocked: the conversation still lives in memory for this page load.
  }
};

export class AgentConversation {
  private state: AgentConversationState;
  private readonly listeners = new Set<() => void>();
  private viewers = 0;
  private disposed = false;
  private idleTimer: number | undefined;
  // Her files, by the turn she sent them with. In memory only: they would overrun sessionStorage.
  private readonly attachments = new Map<number, UploadedFile[]>();
  // Bumped on a new question, so a reply to the old one is dropped when it lands.
  private requestSeq = 0;
  // Set by `open`, which clears `unread` before the popper's open is tracked.
  private openedUnread = false;
  // A promise, so in memory only: after a reload the summary is asked for again when the case is made.
  private caseSummary: { key: string; summary: Promise<AgentCaseSummary> } | undefined;
  private nextId: number;

  constructor(
    private readonly key: string,
    private readonly companyId: number,
    state: AgentConversationState
  ) {
    this.state = state;
    this.nextId = Math.max(FirstTurnId - 1, ...state.turns.map((turn) => turn.id)) + 1;
    this.scheduleIdleEnd();
  }

  attachmentsOf = (turnId: number): UploadedFile[] => this.attachments.get(turnId) ?? [];

  setAttachments = (turnId: number, files: UploadedFile[]) => {
    if (files.length) this.attachments.set(turnId, files);
    else this.attachments.delete(turnId);
  };

  /** Kept under `key`, what it summarized, so a conversation that has grown since is summarized again. */
  keepCaseSummary = (key: string, summary: Promise<AgentCaseSummary>) => {
    // It may never be awaited, and a rejection nobody awaits is reported as unhandled.
    summary.catch(() => undefined);
    this.caseSummary = { key, summary };
  };

  caseSummaryFor = (key: string): Promise<AgentCaseSummary> | undefined =>
    this.caseSummary?.key === key ? this.caseSummary.summary : undefined;

  markChoice = (turnId: number, choice: string) =>
    this.update({ turns: this.state.turns.map((turn) => (turn.id === turnId ? { ...turn, choice } : turn)) });

  removeTurn = (turnId: number) => {
    this.attachments.delete(turnId);
    this.update({ turns: this.state.turns.filter((turn) => turn.id !== turnId) });
  };

  private get trackingContext(): AgentTrackingContext {
    return {
      conversationId: this.state.tracking.conversationId,
      sessionId: this.state.sessionId,
      companyId: this.companyId,
    };
  }

  /** Sends one usage event about this conversation. */
  track = (event: string, attributes?: Record<string, AgentTrackingValue | undefined>) =>
    trackAgent(this.trackingContext, event, attributes);

  /** Updates the usage counts without counting as her activity, so tracking never delays expiry. */
  noteTracking = (change: Partial<AgentConversationTracking>) =>
    this.update({ tracking: { ...this.state.tracking, ...change } }, false);

  popperOpened = () => {
    const continued = this.state.turns.some((turn) => turn.who === "customer");
    this.noteTracking({ popperOpens: this.state.tracking.popperOpens + 1 });
    this.track("popper.opened", { continued, unread: this.openedUnread });
    this.openedUnread = false;
  };

  /**
   * Sends `conversation.ended`, once. A conversation the popper never showed is not counted: the
   * help button creates one on every page to know whether to show the dot.
   */
  private reportEnded = (endedBy: AgentEndedBy) => {
    const { tracking, turns, sentCase } = this.state;
    if (tracking.endedReported || tracking.popperOpens === 0) return;

    this.track("conversation.ended", {
      ending: conversationEnding(turns, !!tracking.agentUnavailable, !!sentCase, !!tracking.reviewShown),
      endedBy,
      actionId: tracking.lastActionId,
      lastOutcome: tracking.lastOutcome,
      caseId: tracking.caseId,
      messageCount: tracking.messageCount,
      popperOpens: tracking.popperOpens,
    });
    this.noteTracking({ endedReported: true });
  };

  // Ends the conversation when it goes idle, without waiting for the next popper open.
  private scheduleIdleEnd() {
    window.clearTimeout(this.idleTimer);
    const wait = this.state.lastActivityAt + IdleExpiryMs - Date.now();
    this.idleTimer = window.setTimeout(
      () => {
        if (this.viewers === 0 && this.isExpired(Date.now())) {
          this.reportEnded("idle");
          // The next open starts over, so her files are not needed again.
          this.attachments.clear();
        }
      },
      Math.max(0, wait) + IdleCheckDelayMs
    );
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.state;

  // An unread reply never expires: the dot on the help button promised it would be there.
  private isExpired = (now: number) =>
    !this.state.pending && !this.state.unread && now - this.state.lastActivityAt > IdleExpiryMs;

  /** Logged out: a reply still in flight must not write the conversation back. */
  dispose = () => {
    this.reportEnded("logout");
    window.clearTimeout(this.idleTimer);
    this.disposed = true;
    this.attachments.clear();
  };

  /** Found expired on load: it ended while the page was away, so it ends as idle, not as a logout. */
  endExpired = () => {
    this.reportEnded("idle");
    this.dispose();
  };

  private update(change: Partial<AgentConversationState>, activity = true) {
    if (this.disposed) return;
    this.state = { ...this.state, ...change, ...(activity ? { lastActivityAt: Date.now() } : {}) };
    // `pending` is never stored, so a change to it alone leaves storage as it is.
    const pendingOnly = Object.keys(change).every((key) => key === "pending");
    if (!pendingOnly) writeStorage(this.key, this.state);
    if (activity) this.scheduleIdleEnd();
    this.listeners.forEach((listener) => listener());
  }

  push = (turn: Omit<AgentTurn, "id" | "at">): number => {
    const id = this.nextId++;
    this.update({
      turns: [...this.state.turns, { ...turn, id, at: new Date() }].slice(-MaxTurns),
      unread: this.state.unread || (turn.who === "agent" && this.viewers === 0),
    });
    return id;
  };

  /** The case is ready for her to look over: the wait ends with it. */
  reviewReady = (review: SupportCaseContent) =>
    this.update({ review, sendFailed: false, pending: null, tracking: { ...this.state.tracking, reviewShown: true } });

  /** Sending failed: the case stays for her to send again. */
  caseFailed = (review: SupportCaseContent) =>
    this.update({ review, sendFailed: true, pending: null, tracking: { ...this.state.tracking, reviewShown: true } });

  caseSent = (sentCase: AgentSentCase) =>
    this.update({
      sentCase,
      review: null,
      sendFailed: false,
      pending: null,
      tracking: { ...this.state.tracking, caseId: sentCase.caseId },
    });

  /**
   * Starts over with an empty question form and, with its first message, a new agent session: Core posts the
   * session's logged conversation on the case, so one session must never carry two questions. The usage
   * tracking starts anew.
   * `popperStaysOpen` false when the popper is about to close, so the empty form she never sees is not counted.
   */
  newQuestion = (popperStaysOpen = true) => {
    this.reportEnded("new_question");
    this.requestSeq++;
    this.attachments.clear();
    this.caseSummary = undefined;
    this.nextId = FirstTurnId;
    const fresh = freshState(Date.now());
    this.update({
      ...fresh,
      // Started with the popper open, so it counts as opened: else its `conversation.ended` is never sent.
      tracking: { ...fresh.tracking, popperOpens: popperStaysOpen && this.viewers > 0 ? 1 : 0 },
    });
  };

  /** Marks a call so its reply can be dropped if a new question was started before it lands. */
  beginRequest = (): number => this.requestSeq;

  isCurrentRequest = (request: number): boolean => request === this.requestSeq;

  /**
   * The question is on screen, so replies are seen as they land. Expiry is applied here, in place:
   * the help button subscribes to this object and would keep reading a replaced one.
   */
  open = () => {
    this.viewers++;
    if (this.viewers === 1 && this.isExpired(Date.now())) {
      this.reportEnded("idle");
      this.nextId = FirstTurnId;
      this.attachments.clear();
      this.caseSummary = undefined;
      this.update(freshState(Date.now()));
    } else if (this.state.unread) {
      this.openedUnread = true;
      this.update({ unread: false });
    }
    // The idle check skips a conversation on screen, so it is scheduled again when the popper closes.
    return () => {
      if (--this.viewers === 0) this.scheduleIdleEnd();
    };
  };

  setPending = (kind: AgentPending["kind"] | null) =>
    this.update({ pending: kind ? { kind, since: Date.now() } : null });

  setSessionId = (sessionId: string) =>
    this.update({ sessionId, sessionEnded: false, sessionRestarted: this.state.sessionId !== null });

  endSession = () => this.update({ sessionEnded: true });
}

const conversations = new Map<string, AgentConversation>();

const freshState = (now: number): AgentConversationState => ({
  turns: [],
  pending: null,
  sessionId: null,
  sessionEnded: false,
  sessionRestarted: false,
  review: null,
  sendFailed: false,
  sentCase: null,
  lastActivityAt: now,
  unread: false,
  tracking: freshTracking(),
});

const freshTracking = (): AgentConversationTracking => ({
  conversationId: window.crypto.randomUUID(),
  popperOpens: 0,
  messageCount: 0,
});

const revive = (stored: StoredConversation): AgentConversationState => ({
  ...stored,
  pending: null,
  unread: stored.unread ?? false,
  review: stored.review ?? null,
  sendFailed: stored.sendFailed ?? false,
  sentCase: stored.sentCase ?? null,
  tracking: stored.tracking ?? freshTracking(),
  turns: stored.turns.map((turn) => ({ ...turn, at: new Date(turn.at) })),
});

const load = (key: string, companyId: number): AgentConversation => {
  const now = Date.now();
  const stored = readStorage(key);

  // `pending` is never stored: after a reload mid-request her question shows unanswered.
  if (stored && (stored.unread || now - stored.lastActivityAt <= IdleExpiryMs)) {
    return new AgentConversation(key, companyId, revive(stored));
  }

  // Expired while the page was away: it ends now, and a new conversation starts in its place.
  if (stored) new AgentConversation(key, companyId, revive(stored)).endExpired();

  return new AgentConversation(key, companyId, freshState(now));
};

const getAgentConversation = (personId: number, companyId: number): AgentConversation => {
  const key = `${StorageKeyPrefix}${personId}:${companyId}`;
  const existing = conversations.get(key);
  if (existing) return existing;

  const conversation = load(key, companyId);
  conversations.set(key, conversation);
  return conversation;
};

// The agent answered that it is switched off; kept for the tab so the popper opens on the FAQ.
const AgentUnavailableKey = `${StorageKeyPrefix}unavailable`;

export const isAgentUnavailable = (): boolean => {
  try {
    return window.sessionStorage.getItem(AgentUnavailableKey) === "true";
  } catch {
    return false;
  }
};

export const markAgentUnavailable = () => {
  try {
    window.sessionStorage.setItem(AgentUnavailableKey, "true");
  } catch {
    // Storage blocked: the popper asks again on its next open.
  }
};

export const clearAgentConversations = () => {
  conversations.forEach((conversation) => conversation.dispose());
  conversations.clear();
  try {
    const { sessionStorage } = window;
    const keys = Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index));
    keys.filter((key) => key?.startsWith(StoragePrefix)).forEach((key) => sessionStorage.removeItem(key as string));
  } catch {
    // Storage blocked: nothing was stored either.
  }
};

export const useAgentConversation = (personId: number, companyId: number) => {
  const conversation = getAgentConversation(personId, companyId);
  const state = React.useSyncExternalStore(conversation.subscribe, conversation.getSnapshot);
  return { conversation, state };
};
